import React, { createContext, useContext, useReducer, useMemo, useEffect } from "react";

const CartContext = createContext(null);

const LS_KEY = "sunukr_cart_v1";


function load() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        return raw ? JSON.parse(raw) : { items: [] };
    } catch {
        return { items: [] };
    }
}

function save(state) {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
}

function reducer(state, action) {
    switch (action.type) {
        case "ADD": {
            const { sku, title, price, img, unit, quantity = 1 } = action.payload;
            const items = [...state.items];
            const i = items.findIndex(x => x.sku === sku);
            if (i >= 0) items[i] = { ...items[i], quantity: items[i].quantity + quantity };
            else items.push({ sku, title, price, img, unit, quantity });
            const next = { items };
            save(next);
            return next;
        }
        case "INC": {
            const items = state.items.map(x => x.sku === action.sku ? { ...x, quantity: x.quantity + 1 } : x);
            const next = { items };
            save(next);
            return next;
        }
        case "DEC": {
            const items = state.items
                .map(x => x.sku === action.sku ? { ...x, quantity: Math.max(1, x.quantity - 1) } : x);
            const next = { items };
            save(next);
            return next;
        }
        case "REMOVE": {
            const items = state.items.filter(x => x.sku !== action.sku);
            const next = { items };
            save(next);
            return next;
        }
        case "CLEAR": {
            const next = { items: [] };
            save(next);
            return next;
        }
        default:
            return state;
    }
}

export default function CartProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, undefined, load);

    const totalQty = useMemo(() => state.items.reduce((s, i) => s + i.quantity, 0), [state]);
    const totalSum = useMemo(() => state.items.reduce((s, i) => s + i.quantity * i.price, 0), [state]);

    const value = useMemo(() => ({
        items: state.items,
        totalQty,
        totalSum,
        add: (payload) => dispatch({ type: "ADD", payload }),
        inc: (sku) => dispatch({ type: "INC", sku }),
        dec: (sku) => dispatch({ type: "DEC", sku }),
        remove: (sku) => dispatch({ type: "REMOVE", sku }),
        clear: () => dispatch({ type: "CLEAR" }),
    }), [state, totalQty, totalSum]);

    useEffect(() => { /* just to persist on first mount */ }, []);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
